// Shared helpers for rendering audit-log rows (Trace view + Excel export).
//
// Time entries carry a rich `details` shape (owner, project, date, times, or a
// before/after diff). Everything else — the project/client Intelligence edits —
// carries `{ projectCode, projectName }` or `{ clientName }` plus a few keys
// describing what changed. These helpers turn the latter into a readable
// "Item" label and a one-line summary.

export const ENTITY_LABEL = {
  time_entry: 'Time entry',
  project_profile: 'Project profile',
  project_schedule: 'Project schedule',
  project_memory: 'Project memory',
  project_stakeholders: 'Stakeholders',
  project_resources: 'Resources',
  project_payment_milestones: 'Payment milestones',
  project_interaction: 'Interaction',
  client_profile: 'Client profile',
  client_relationship: 'Client relationship',
  client_contacts: 'Client contacts',
  client_actions: 'Client actions',
  client_opportunities: 'Client opportunities',
  client_platform_orgs: 'Platform organizations',
  client_usage: 'Client usage',
  usage_run: 'Usage run (all clients)',
  timer: 'Timer',
};

export function traceItemLabel(entityType) {
  return ENTITY_LABEL[entityType] || entityType || '—';
}

// The project or client the change was made against.
export function traceContext(details) {
  const d = details || {};
  if (d.clientName) return d.clientName;
  return [d.projectCode, d.projectName].filter(Boolean).join(' ');
}

const asDate = (value) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value))
    return value.slice(0, 10);
  return value ?? '';
};

// Origin of an audit row: the MCP server tags every row it writes with
// `via: 'mcp'` + `mcpClient` (mcp/audit.js#mcpAudit); anything else came from
// a GUI-driven API call. Rendered as its own badge (Trace.jsx), independent
// of traceSummary, so it shows up consistently regardless of which entity
// type or summary branch a row takes.
export function traceOrigin(details) {
  const d = details || {};
  if (d.via === 'mcp') return { label: 'MCP', client: d.mcpClient || null };
  return { label: 'Web', client: null };
}

// A short description of a non-time-entry audit row. Empty string for
// time entries (their info lives in the dedicated columns / diff view).
export function traceSummary(entityType, details) {
  const d = details || {};
  if (entityType === 'time_entry') return '';
  if (d.title) return `“${d.title}”`;

  if (entityType === 'project_schedule') {
    const parts = [];
    if ('startDate' in d) parts.push(`start ${d.startDate || '—'}`);
    if ('endDate' in d) parts.push(`end ${d.endDate || '—'}`);
    return parts.join(' · ');
  }
  if (entityType === 'project_profile' && 'lifecycleStage' in d) {
    const phase = d.lifecycleStage
      ? `lifecycle phase → ${d.lifecycleStage}`
      : 'lifecycle phase cleared';
    return d.roles ? `${phase} · ${d.roles}` : phase;
  }
  if (entityType === 'project_profile' && d.sections) {
    const names = {
      lifecycleStage: 'lifecycle phase',
      accountManagerIds: 'account managers',
      supportTeamIds: 'support team',
      technicalLeadIds: 'technical leads',
    };
    return d.sections
      .split(', ')
      .map((s) => names[s] || s)
      .join(', ');
  }
  if (
    (entityType === 'project_profile' || entityType === 'client_profile') &&
    d.fields
  ) {
    return d.fields;
  }
  if (entityType === 'client_profile' && d.tier) return `tier ${d.tier}`;
  if (entityType === 'client_relationship' && d.sections) {
    const names = {
      health: 'health',
      healthNotes: 'health notes',
      lastMeaningfulInteraction: 'last interaction',
      lastStrategicReview: 'last review',
      nextCheckpoint: 'next checkpoint',
      contactCadence: 'contact cadence',
    };
    return d.sections
      .split(', ')
      .map((s) => names[s] || s)
      .join(', ');
  }
  if (entityType === 'client_relationship') {
    return d.health ? `health → ${d.health}` : 'relationship updated';
  }
  if (entityType === 'project_memory' && 'step' in d) {
    return `“${d.step}” ${d.complete ? 'done' : 'reopened'}`;
  }
  if (entityType === 'project_memory' && d.sections) {
    const names = {
      currentStatus: 'current status',
      playbook: 'playbook',
      steps: 'next steps',
      lifecycleMilestones: 'lifecycle',
    };
    return d.sections
      .split(', ')
      .map((s) => names[s] || s)
      .join(', ');
  }
  if (entityType === 'timer') {
    const parts = [];
    if (d.description) parts.push(`“${d.description}”`);
    if (d.startedAt)
      parts.push(`started ${d.startedAt.slice(0, 16).replace('T', ' ')} UTC`);
    if (d.endAt)
      parts.push(`end ${d.endAt.slice(0, 16).replace('T', ' ')} UTC`);
    if (d.entries)
      parts.push(`${d.entries} entr${d.entries === 1 ? 'y' : 'ies'} saved`);
    return parts.join(' · ');
  }
  if (entityType === 'usage_run') {
    const unmapped = d.unmapped
      ? ` · ${d.unmapped} unlinked organization(s)`
      : '';
    return `${d.source} ${d.month} recomputed for all clients · ${d.clients} with calls${unmapped}`;
  }
  if (entityType === 'client_usage') {
    return `${d.source} ${d.month} recomputed`;
  }
  if (typeof d.count === 'number') {
    return `${d.count} item${d.count === 1 ? '' : 's'}`;
  }
  // Fall back to the leftover keys (drop the context ones and origin
  // metadata, which traceOrigin renders separately).
  const skip = new Set([
    'projectCode',
    'projectName',
    'clientName',
    'via',
    'mcpClient',
  ]);
  const rest = Object.entries(d).filter(([k]) => !skip.has(k));
  return rest.map(([k, v]) => `${k} ${asDate(v)}`).join(' · ');
}
